import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Card, Container, Row, Col, Alert, Form, InputGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Customer1SaleBatchDetailsModal from '../components/Customer1SaleBatchDetailsModal';
import Loader from '../components/Loader';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const Mijozlar = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { user, token } = useAuth();
  
  // Search states
  const [searchName, setSearchName] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch all sales data once
  const fetchSales = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/customer1-sales?limit=1000', {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        }
      }); // Get all data for client-side filtering
      if (!res.ok) throw new Error('Mijozlar sotuvlarini yuklashda xatolik!');
      const data = await res.json();
      const salesData = data.items || data || [];
      setSales(salesData);
    } catch (err) {
      setError(err.message);
      setSales([]);
      toast.error('Xatolik: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  // Client-side filtering with useMemo for performance
  const filteredSales = useMemo(() => {
    let filtered = sales;

    if (searchName.trim()) {
      filtered = filtered.filter(sale => 
        sale.customer_name && 
        sale.customer_name.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (searchPhone.trim()) {
      filtered = filtered.filter(sale => 
        sale.customer_phone && 
        sale.customer_phone.includes(searchPhone)
      );
    }

    return filtered;
  }, [sales, searchName, searchPhone]);

  // Pagination calculation
  const totalItems = filteredSales.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSales.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to first page when filtering
  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, searchPhone]);

  const handleShowDetails = (batch) => {
    setSelectedBatch(batch);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBatch(null);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchName('');
    setSearchPhone('');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('uz-UZ');
  };

  const formatPrice = (price, currency = 'UZS') => {
    if (!price) return '0';
    return Number(price).toLocaleString() + ' ' + currency;
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Mijozlar Sotuvlari</h4>
            </Card.Header>
            <Card.Body>
              {/* Search Filters */}
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Mijoz Nomi</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="bi bi-person"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Mijoz nomini kiriting..."
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Telefon Raqami</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="bi bi-telephone"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Telefon raqamini kiriting..."
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Search Results Info and Clear Button */}
              {(searchName || searchPhone) && (
                <Row className="mb-3">
                  <Col md={6}>
                    <small className="text-muted">
                      <i className="bi bi-search me-1"></i>
                      {filteredSales.length} ta natija topildi
                    </small>
                  </Col>
                  <Col md={6} className="text-end">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={clearSearch}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Qidiruvni tozalash
                    </Button>
                  </Col>
                </Row>
              )}

              {error ? (
                <Alert variant="danger">
                  <Alert.Heading>Xatolik!</Alert.Heading>
                  <p>{error}</p>
                  <Button variant="outline-danger" onClick={fetchSales}>
                    Qayta urinish
                  </Button>
                </Alert>
              ) : filteredSales.length === 0 ? (
                <Alert variant="info">
                  <Alert.Heading>
                    {sales.length === 0 ? 'Ma\'lumot yo\'q' : 'Qidiruv natijasi'}
                  </Alert.Heading>
                  <p>
                    {sales.length === 0 
                      ? 'Hozircha mijozlar sotuvlari mavjud emas.' 
                      : 'Qidiruv kriteriyalariga mos keladigan natijalar topilmadi.'
                    }
                  </p>
                </Alert>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table striped bordered hover className="mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th>Mijoz Nomi</th>
                          <th>Telefon Raqami</th>
                          <th>Jami Narxi</th>
                          <th>Amallar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((sale, index) => (
                          <tr key={sale.SellBatchID || index}>
                            <td className="align-middle">
                              <strong>{sale.customer_name || "Noma'lum"}</strong>
                            </td>
                            <td className="align-middle">
                              {sale.customer_phone || '-'}
                            </td>
                            <td className="align-middle text-end">
                              <span className="fw-bold text-success">
                                {formatPrice(Number(sale.total_price) + (sale.isdelivery ? Number(sale.delivery_price) : 0), sale.Currency)}
                              </span>
                              <br />
                              <small className="text-muted">
                                {formatDate(sale.SaleDate)}
                              </small>
                            </td>
                            <td className="align-middle text-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleShowDetails(sale)}
                              >
                                Tafsilotlar
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  
                  {/* Pagination */}
                  {filteredSales.length > 0 && (
                    <div className="mt-3">
                      <Pagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sale Batch Details Modal */}
      {selectedBatch && (
        <Customer1SaleBatchDetailsModal
          batch={selectedBatch}
          show={showModal}
          onHide={handleCloseModal}
        />
      )}
    </Container>
  );
};

export default Mijozlar;